/**
 * PendingDecisionService - Business logic for pending decisions
 *
 * Manages pending decisions state and provides reactive access via LiveData.
 * Integrates with the notification system for real-time updates.
 */
import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { exhaustMap, tap } from 'rxjs';

import type {
  PendingDecision,
  PendingDecisionStore,
} from '../stores/pending-decision';

export class PendingDecisionService extends Service {
  /** All pending decisions for the current workspace */
  readonly pendingDecisions$ = new LiveData<PendingDecision[]>([]);

  /** Count of pending decisions (status === 'pending') */
  readonly count$ = this.pendingDecisions$.map(
    decisions => decisions.filter(d => d.status === 'pending').length
  );

  /** Loading state */
  readonly isLoading$ = new LiveData(false);

  /** Error state */
  readonly error$ = new LiveData<Error | null>(null);

  /** Current workspace ID being tracked */
  private currentWorkspaceId: string | null = null;

  constructor(private readonly store: PendingDecisionStore) {
    super();
  }

  /**
   * Load/reload pending decisions for a workspace.
   * Creates an effect that can be triggered to refresh the data.
   */
  readonly revalidate = effect(
    exhaustMap(() => {
      if (!this.currentWorkspaceId) {
        return [];
      }

      return fromPromise(signal =>
        this.store.getPendingDecisions(this.currentWorkspaceId!, signal)
      ).pipe(
        tap(decisions => {
          this.pendingDecisions$.next(decisions);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => this.isLoading$.setValue(true)),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  /**
   * Set the workspace ID and trigger initial load.
   */
  setWorkspaceId(workspaceId: string) {
    if (this.currentWorkspaceId !== workspaceId) {
      this.currentWorkspaceId = workspaceId;
      this.pendingDecisions$.setValue([]);
      this.error$.setValue(null);
      this.revalidate();
    }
  }

  /**
   * Add a pending decision (called from WebSocket handler).
   */
  addDecision(decision: PendingDecision) {
    const current = this.pendingDecisions$.value;
    // Avoid duplicates
    if (!current.find(d => d.id === decision.id)) {
      this.pendingDecisions$.next([decision, ...current]);
    }
  }

  /**
   * Remove a pending decision (called from WebSocket handler when resolved/skipped).
   */
  removeDecision(decisionId: string) {
    this.pendingDecisions$.next(
      this.pendingDecisions$.value.filter(d => d.id !== decisionId)
    );
  }

  /**
   * Update a pending decision's status.
   */
  updateDecision(decision: PendingDecision) {
    this.pendingDecisions$.next(
      this.pendingDecisions$.value.map(d =>
        d.id === decision.id ? decision : d
      )
    );
  }

  /**
   * Resolve a pending decision by selecting a candidate.
   */
  async resolve(id: string, resolvedWith: string): Promise<void> {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace context set for pending decision resolve');
    }
    const result = await this.store.resolvePendingDecision(
      this.currentWorkspaceId,
      id,
      resolvedWith
    );
    // Update local state
    this.updateDecision(result);
  }

  /**
   * Skip a pending decision (leave relation unlinked).
   */
  async skip(id: string): Promise<void> {
    if (!this.currentWorkspaceId) {
      throw new Error('No workspace context set for pending decision skip');
    }
    const result = await this.store.skipPendingDecision(
      this.currentWorkspaceId,
      id
    );
    // Update local state
    this.updateDecision(result);
  }

  /**
   * Reset the service state.
   */
  reset() {
    this.pendingDecisions$.setValue([]);
    this.isLoading$.setValue(false);
    this.error$.setValue(null);
    this.currentWorkspaceId = null;
    this.revalidate.reset();
  }
}
